import { useState } from "react";
import { motion } from "framer-motion";
import {
  Target,
  Megaphone,
  BookOpen,
  MonitorSmartphone,
  Globe,
  Leaf,
} from "lucide-react";
import { Page, PageHero, Eyebrow } from "@/components/PageShell";
import { SERVICES, IMAGES } from "@/data/site";

const ICONS = [Target, Megaphone, BookOpen, MonitorSmartphone, Globe, Leaf];

const REGIONS = [
  { key: "latam", label: "Latin America", weeks: 14, reach: "18 markets" },
  { key: "africa", label: "Africa", weeks: 16, reach: "12 markets" },
  { key: "europe", label: "Europe & Switzerland", weeks: 10, reach: "22 markets" },
  { key: "global", label: "Global Mandate", weeks: 20, reach: "40+ markets" },
];

const CHANNELS = [
  "Institutional Roadshow",
  "Digital Investor Portal",
  "Print & Pitchbook Suite",
  "PR & Media Programme",
  "Private Client Events",
];

function Calculator() {
  const [capital, setCapital] = useState(250);
  const [region, setRegion] = useState(REGIONS[0]);
  const [channels, setChannels] = useState([CHANNELS[0], CHANNELS[2]]);

  const toggle = (c) =>
    setChannels((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );

  const scaleWeeks = capital > 500 ? 4 : capital > 200 ? 2 : capital > 50 ? 1 : 0;
  const weeks = region.weeks + scaleWeeks + Math.max(0, channels.length - 2);
  const tier =
    capital > 500 ? "Sovereign Mandate" : capital > 200 ? "Institutional Flagship" : capital > 50 ? "Growth Placement" : "Boutique Placement";

  return (
    <div
      className="border border-[rgba(197,160,89,0.25)] bg-[#162248] p-8 lg:p-12"
      data-testid="campaign-calculator"
    >
      <Eyebrow>Interactive</Eyebrow>
      <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl font-normal text-[#F8F9FA]">
        Campaign Impact Simulator
      </h2>
      <p className="mt-3 text-sm text-[#A0AAB2] max-w-xl">
        Model a capital target, region, and channel mix to see the campaign profile a
        Mirabaud project of that scale would typically receive.
      </p>

      <div className="mt-10 grid lg:grid-cols-2 gap-12">
        <div className="space-y-9">
          <div>
            <div className="flex justify-between items-baseline mb-3">
              <label className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                Capital Target
              </label>
              <span className="font-serif-display text-2xl text-[#E6C687]" data-testid="calculator-capital-display">
                ${capital}M
              </span>
            </div>
            <input
              type="range"
              min="10"
              max="1000"
              step="10"
              value={capital}
              onChange={(e) => setCapital(Number(e.target.value))}
              className="gold-slider w-full"
              data-testid="calculator-input-slider"
            />
            <div className="flex justify-between mt-2 text-[10px] font-mono-label tracking-[0.15em] text-[#6C7A89]">
              <span>$10M</span>
              <span>$1B</span>
            </div>
          </div>

          <div>
            <label className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89] block mb-3">
              Target Region
            </label>
            <div className="grid grid-cols-2 gap-3">
              {REGIONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRegion(r)}
                  data-testid={`calculator-region-${r.key}`}
                  className={`px-4 py-3 text-sm border transition-all duration-300 text-left ${
                    region.key === r.key
                      ? "border-[#C5A059] bg-[rgba(197,160,89,0.12)] text-[#E6C687]"
                      : "border-[rgba(197,160,89,0.2)] text-[#A0AAB2] hover:border-[rgba(197,160,89,0.45)]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89] block mb-3">
              Campaign Channels
            </label>
            <div className="flex flex-wrap gap-2.5">
              {CHANNELS.map((c) => (
                <button
                  key={c}
                  onClick={() => toggle(c)}
                  data-testid={`calculator-channel-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-4 py-2 text-xs border transition-all duration-300 ${
                    channels.includes(c)
                      ? "border-[#C5A059] bg-[rgba(197,160,89,0.12)] text-[#E6C687]"
                      : "border-[rgba(197,160,89,0.2)] text-[#A0AAB2] hover:border-[rgba(197,160,89,0.45)]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border border-[rgba(197,160,89,0.2)] bg-[#0B132B] p-8 flex flex-col justify-center" data-testid="calculator-result">
          <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
            Projected Campaign Profile
          </span>
          <h3 className="mt-3 font-serif-display text-3xl text-[#E6C687]" data-testid="calculator-tier">
            {tier}
          </h3>
          <dl className="mt-8 space-y-5">
            <div className="flex justify-between border-b border-[rgba(197,160,89,0.15)] pb-4">
              <dt className="text-sm text-[#A0AAB2]">Engagement window</dt>
              <dd className="text-sm font-semibold text-[#F8F9FA]" data-testid="calculator-weeks">
                {weeks} weeks
              </dd>
            </div>
            <div className="flex justify-between border-b border-[rgba(197,160,89,0.15)] pb-4">
              <dt className="text-sm text-[#A0AAB2]">Investor reach</dt>
              <dd className="text-sm font-semibold text-[#F8F9FA]">{region.reach}</dd>
            </div>
            <div className="flex justify-between border-b border-[rgba(197,160,89,0.15)] pb-4">
              <dt className="text-sm text-[#A0AAB2]">Active channels</dt>
              <dd className="text-sm font-semibold text-[#F8F9FA]" data-testid="calculator-channels-count">
                {channels.length} of {CHANNELS.length}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-[#A0AAB2]">Region focus</dt>
              <dd className="text-sm font-semibold text-[#F8F9FA]">{region.label}</dd>
            </div>
          </dl>
          <p className="mt-8 text-xs leading-relaxed text-[#6C7A89]">
            Indicative modelling only. Every Mirabaud mandate is structured individually
            with the Group's investment teams.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <Page testid="services-page">
      <PageHero
        eyebrow="What We Do"
        title="Marketing engineered for capital."
        subtitle="Six disciplines, one mandate: connecting Mirabaud investment projects with the world's institutional and private capital."
        image={IMAGES.investmentBuilding}
        testid="services-hero"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="services-grid">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="border border-[rgba(197,160,89,0.2)] bg-[#162248] p-8 card-hover"
                data-testid={`service-card-${i}`}
              >
                <div className="w-11 h-11 border border-[rgba(197,160,89,0.4)] flex items-center justify-center">
                  <Icon size={19} className="text-[#C5A059]" />
                </div>
                <h3 className="mt-6 font-serif-display text-xl text-[#F8F9FA]">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#A0AAB2]">{s.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 lg:pb-32" data-testid="services-calculator-section">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Calculator />
        </motion.div>
      </section>
    </Page>
  );
}
